/**
 * ErrorMessage Component — Phase 4a
 *
 * Displays error messages to users in a friendly way.
 * Used to show 403 Forbidden and other API errors.
 */

import React from 'react';

export function ErrorMessage({ error, onDismiss }) {
  if (!error) return null;

  // Extract processed error info
  const title = error.processedError?.userMessage || 'Error';
  const message = error.processedError?.userDetail || error.message || 'An error occurred';
  const statusCode = error.processedError?.statusCode || error.response?.status;

  // Determine error color based on status code
  let backgroundColor = '#fee';
  let borderColor = '#f99';
  let iconEmoji = '⚠️';

  if (statusCode === 403) {
    backgroundColor = '#fef3c7'; // Amber
    borderColor = '#f59e0b';
    iconEmoji = '🔒';
  } else if (statusCode === 401) {
    backgroundColor = '#fee';
    borderColor = '#f99';
    iconEmoji = '🔐';
  } else if (statusCode >= 500) {
    backgroundColor = '#fee';
    borderColor = '#f99';
    iconEmoji = '❌';
  }

  return (
    <div
      style={{
        backgroundColor,
        borderLeft: `4px solid ${borderColor}`,
        padding: '12px 16px',
        marginBottom: '16px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}
    >
      <div style={{ fontSize: '20px', marginTop: '2px' }}>{iconEmoji}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{title}</div>
        <div style={{ fontSize: '14px', color: '#555' }}>{message}</div>
        {statusCode && (
          <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            Error {statusCode}
          </div>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '16px',
            cursor: 'pointer',
            color: '#999',
            padding: '0',
            marginTop: '-2px',
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * PermissionDeniedMessage Component
 *
 * Specialized component for 403 Forbidden errors
 */
export function PermissionDeniedMessage({ action, onDismiss }) {
  return (
    <div
      style={{
        backgroundColor: '#fef3c7',
        borderLeft: '4px solid #f59e0b',
        padding: '12px 16px',
        marginBottom: '16px',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}
    >
      <div style={{ fontSize: '20px', marginTop: '2px' }}>🔒</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Permission Denied</div>
        <div style={{ fontSize: '14px', color: '#555' }}>
          {action ? `You don't have permission to ${action}.` : "You don't have permission to perform this action."}
          {' '}Please contact your administrator if you believe this is an error.
        </div>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '16px',
            cursor: 'pointer',
            color: '#999',
            padding: '0',
            marginTop: '-2px',
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default ErrorMessage;
